import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,

  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Pagination,
  Stack,
  Link,
} from '@mui/material';
import {
  Search,

  People,
  CheckCircle,
  RadioButtonUnchecked,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AppDispatch, RootState } from '../../store';
import { fetchStudentsPaginated, updateStudentAsync, deleteStudentAsync } from '../../store/slices/studentSlice';
import { fetchSchools } from '../../store/slices/schoolSlice';
import { Student as StudentType } from '../../types';

const SubjectChip: React.FC<{ subject: string; isSubscribed: boolean }> = ({ subject, isSubscribed }) => (
  <Chip
    label={subject}
    size="small"
    variant={isSubscribed ? "filled" : "outlined"}
    color={isSubscribed ? "success" : "default"}
    icon={isSubscribed ? <CheckCircle fontSize="small" /> : <RadioButtonUnchecked fontSize="small" />}
    sx={{
      m: 0.5,
      fontSize: '0.75rem',
      height: 24,
      minWidth: 80,
      '& .MuiChip-icon': {
        color: isSubscribed ? 'white' : 'text.secondary',
      },
    }}
  />
);

/**
 * Last ten digits of a phone number. Mirrors PhoneNumbers.last10Digits in school-service,
 * which is what actually sets the value; this is only so the form can show it.
 */
const last10Digits = (phoneNumber?: string): string => {
  const digits = (phoneNumber || '').replace(/[^0-9]/g, '');
  return digits.length >= 10 ? digits.slice(-10) : digits;
};

const StudentsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { paginatedStudents: students, pagination, isLoading, error } = useSelector((state: RootState) => state.students);
  const { schools } = useSelector((state: RootState) => state.schools);
  const { selected: selectedYear } = useSelector((state: RootState) => state.academicYear);
  
  const [openDialog, setOpenDialog] = useState(false);
  const [editingStudent, setEditingStudent] = useState<StudentType | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(100);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phoneNumber: '',
    securityPin: '',
    grade: '',
    schoolId: '',
    userId: '',
    subjects: [] as string[],
  });

  useEffect(() => {
    loadStudents();
    dispatch(fetchSchools({ academicYear: selectedYear }));
  }, [dispatch, currentPage, searchTerm, selectedYear]);

  const loadStudents = () => {
    dispatch(fetchStudentsPaginated({
      academicYear: selectedYear,
      page: currentPage - 1, // Backend uses 0-based indexing
      size: pageSize,
      search: searchTerm.trim() || undefined,
    }));
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleOpenDialog = (student: StudentType) => {
    setEditingStudent(student);
    setFormData({
      fullName: student.fullName || student.name || '',
      email: student.email || '',
      phoneNumber: student.phoneNumber || student.phone || '',
      securityPin: student.securityPin || '',
      grade: student.grade || '',
      schoolId: student.schoolId || '',
      userId: student.userId || '',
      subjects: student.subjects || [],
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingStudent(null);
    setFormData({
      fullName: '',
      email: '',
      phoneNumber: '',
      securityPin: '',
      grade: '',
      schoolId: '',
      userId: '',
      subjects: [],
    });
  };

  const handleSubmit = async () => {
    try {
      if (editingStudent) {
        await dispatch(updateStudentAsync({
          id: editingStudent.id,
          studentData: formData
        })).unwrap();
      }
      handleCloseDialog();
      loadStudents(); // Reload the current page
    } catch (error) {
      console.error('Failed to save student:', error);
      // Error is already handled by the slice
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this student?')) {
      try {
        await dispatch(deleteStudentAsync(id)).unwrap();
        loadStudents(); // Reload the current page
      } catch (error) {
        console.error('Failed to delete student:', error);
        // Error is already handled by the slice
      }
    }
  };

  const getSchoolName = (schoolId: string) => {
    const school = schools.find(s => s.id === schoolId);
    return school ? school.name : 'Unknown School';
  };

  const handleSchoolClick = (schoolId: string) => {
    if (schoolId) {
      navigate(`/schools/${schoolId}/students`);
    }
  };

  const allSubjects = ['Math', 'Science', 'English'];

  if (isLoading && students.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography>Loading students...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            LiveOlympiad Students
          </Typography>
          <Typography variant="body1" color="text.secondary">
            View and manage all registered students across all schools
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Search and Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TextField
            fullWidth
            placeholder="Search students by name, phone, security pin, or grade..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </CardContent>
      </Card>

      {/* Students Table */}
      <Card>
        <CardContent>
          <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Student Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Contact</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Grade</TableCell>
                  <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>School</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Security Pin</TableCell>
                  <TableCell sx={{ fontWeight: 600, minWidth: 180 }}>Subjects</TableCell>


                </TableRow>
              </TableHead>
              <TableBody>
                {students.map((student) => (
                  <TableRow key={student.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <People sx={{ mr: 2, color: 'primary.main' }} />
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {student.fullName || student.name}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {(student.phoneNumber || student.phone || 'No phone').replace(/^\+91/, '')}
                        </Typography>
                        {student.email && (
                          <Typography variant="caption" color="text.secondary">
                            {student.email}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={student.grade || 'N/A'}
                        variant="outlined"
                        size="small"
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Link
                        component="button"
                        variant="body2"
                        onClick={() => handleSchoolClick(student.schoolId || '')}
                        sx={{
                          textDecoration: 'none',
                          color: 'primary.main',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        {getSchoolName(student.schoolId || '')}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={student.securityPin || 'N/A'}
                        variant="outlined"
                        size="small"
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {allSubjects.map((subject) => (
                          <SubjectChip
                            key={subject}
                            subject={subject}
                            isSubscribed={student.subjects?.includes(subject) || false}
                          />
                        ))}
                      </Box>
                    </TableCell>


                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, pagination.totalElements)} of {pagination.totalElements} students
                </Typography>
                <Pagination
                  count={pagination.totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Edit Student Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Edit Student
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
            <Box sx={{ flex: '1 1 100%', minWidth: 0 }}>
              <TextField
                fullWidth
                label="Full Name"
                value={formData.fullName}
                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                sx={{ mb: 2 }}
              />
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
              <TextField
                fullWidth
                label="Login ID"
                value={last10Digits(formData.phoneNumber) || ''}
                InputProps={{ readOnly: true }}
                sx={{ mb: 2 }}
                helperText={
                  formData.phoneNumber
                    ? 'Derived from the phone number — the student signs in with this'
                    : 'Set automatically from the phone number'
                }
              />
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                sx={{ mb: 2 }}
              />
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phoneNumber}
                onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })}
                sx={{ mb: 2 }}
              />
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
              <TextField
                fullWidth
                label="Security Pin"
                value={formData.securityPin}
                onChange={(e) => setFormData({ ...formData, securityPin: e.target.value.replace(/[^0-9]/g, '') })}
                sx={{ mb: 2 }}
                inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                helperText="6-digit security code"
              />
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Grade</InputLabel>
                <Select
                  value={formData.grade}
                  label="Grade"
                  onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                >
                  <MenuItem value="Class-01">Class-01</MenuItem>
                  <MenuItem value="Class-02">Class-02</MenuItem>
                  <MenuItem value="Class-03">Class-03</MenuItem>
                  <MenuItem value="Class-04">Class-04</MenuItem>
                  <MenuItem value="Class-05">Class-05</MenuItem>
                  <MenuItem value="Class-06">Class-06</MenuItem>
                  <MenuItem value="Class-07">Class-07</MenuItem>
                  <MenuItem value="Class-08">Class-08</MenuItem>
                  <MenuItem value="Class-09">Class-09</MenuItem>
                  <MenuItem value="Class-10">Class-10</MenuItem>
                  <MenuItem value="Class-11">Class-11</MenuItem>
                  <MenuItem value="Class-12">Class-12</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Subjects</InputLabel>
                <Select
                  multiple
                  value={formData.subjects}
                  label="Subjects"
                  onChange={(e) => setFormData({ ...formData, subjects: e.target.value as string[] })}
                >
                  <MenuItem value="Math">Math</MenuItem>
                  <MenuItem value="Science">Science</MenuItem>
                  <MenuItem value="English">English</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>School</InputLabel>
                <Select
                  value={formData.schoolId}
                  label="School"
                  onChange={(e) => setFormData({ ...formData, schoolId: e.target.value })}
                >
                  {schools.map((school) => (
                    <MenuItem key={school.id} value={school.id}>
                      {school.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              },
            }}
          >
            Update Student
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StudentsPage; 